/**
 * Hyperbolic triangle groups.
 *
 * The (p, q, r) triangle has angles π/p, π/q, π/r. It exists in the hyperbolic plane exactly
 * when 1/p + 1/q + 1/r < 1, and the reflections in its three sides generate a discrete group
 * whose images of the triangle tile the plane. We place it in the Poincaré disk with
 *
 *   P = 0               (angle π/p, the centre of a tile),
 *   R on the real axis  (angle π/r; for {p,q} tilings r = 2 and R is an edge midpoint),
 *   Q on the ray arg z = π/p   (angle π/q; for {p,q} tilings a vertex of the tiling).
 *
 * Mirrors (indexed 0, 1, 2 throughout the app and the shader):
 *   m0 = PR  the real axis                       — F lies where Im z ≥ 0
 *   m1 = PQ  the diameter at angle π/p           — F lies where arg z ≤ π/p
 *   m2 = QR  a circle orthogonal to |z| = 1      — F lies outside the circle
 *
 * Rotation generators: x = m0·m1 about P (order p), y = m1·m2 about Q (order q),
 * z = m2·m0 about R (order r), with x·y·z = 1.
 */
import { type Complex, cx, fromPolar } from './complex';
import {
  type Isometry,
  IDENTITY,
  REFLECT_REAL,
  compose,
  reflectCircle,
  reflectLine,
} from './mobius';

export type GeometryKind = 'hyperbolic' | 'euclidean' | 'spherical';

export interface TriangleClass {
  kind: GeometryKind;
  /** Angle sum in degrees. */
  angleSumDeg: number;
  /** Number of triangles in the spherical tiling (only when kind === 'spherical'). */
  sphericalCount?: number;
}

/**
 * Classifies (p, q, r) exactly in integer arithmetic:
 * 1/p + 1/q + 1/r < 1  ⇔  qr + pr + pq < pqr.
 */
export function classifyTriangle(p: number, q: number, r: number): TriangleClass {
  const lhs = q * r + p * r + p * q;
  const rhs = p * q * r;
  const angleSumDeg = 180 * (1 / p + 1 / q + 1 / r);
  if (lhs < rhs) return { kind: 'hyperbolic', angleSumDeg };
  if (lhs === rhs) return { kind: 'euclidean', angleSumDeg };
  // A spherical triangle of excess E = π(1/p + 1/q + 1/r − 1) has area E; the sphere has area 4π.
  return { kind: 'spherical', angleSumDeg, sphericalCount: Math.round((4 * rhs) / (lhs - rhs)) };
}

export const MIN_ORDER = 2;
export const MAX_ORDER = 24;

export function isValidOrder(n: number): boolean {
  return Number.isInteger(n) && n >= MIN_ORDER && n <= MAX_ORDER;
}

export interface TriangleGroup {
  p: number;
  q: number;
  r: number;
  P: Complex;
  Q: Complex;
  R: Complex;
  /** Outward unit normal of m1 (points away from F). */
  n1: Complex;
  /** Circle carrying m2. */
  center: Complex;
  radius: number;
  /** Hyperbolic side lengths. */
  lengthPR: number;
  lengthPQ: number;
  lengthQR: number;
  /** Area of the triangle, π(1 − 1/p − 1/q − 1/r) by Gauss–Bonnet. */
  area: number;
  /** The three reflections as disk isometries. */
  mirrors: [Isometry, Isometry, Isometry];
  /** Klein-model coordinates of Q and R (P is the origin), used to normalise strokes. */
  kleinQ: Complex;
  kleinR: number;
}

export function buildTriangleGroup(p: number, q: number, r: number): TriangleGroup {
  if (![p, q, r].every(isValidOrder)) {
    throw new RangeError(`Orders must be integers in [${MIN_ORDER}, ${MAX_ORDER}]`);
  }
  const cls = classifyTriangle(p, q, r);
  if (cls.kind !== 'hyperbolic') {
    throw new RangeError(`(${p}, ${q}, ${r}) is ${cls.kind}, not hyperbolic`);
  }
  const A = Math.PI / p;
  const B = Math.PI / q;
  const C = Math.PI / r;
  // Hyperbolic law of cosines for angles: cosh(side) = (cos opp + cos adj1 cos adj2)/(sin adj1 sin adj2)
  const coshPR = (Math.cos(B) + Math.cos(A) * Math.cos(C)) / (Math.sin(A) * Math.sin(C));
  const coshPQ = (Math.cos(C) + Math.cos(A) * Math.cos(B)) / (Math.sin(A) * Math.sin(B));
  const coshQR = (Math.cos(A) + Math.cos(B) * Math.cos(C)) / (Math.sin(B) * Math.sin(C));
  const lengthPR = Math.acosh(coshPR);
  const lengthPQ = Math.acosh(coshPQ);
  const lengthQR = Math.acosh(coshQR);
  const Rr = Math.tanh(lengthPR / 2);
  const R = cx(Rr, 0);
  const Q = fromPolar(Math.tanh(lengthPQ / 2), A);
  // Circle through Q and R orthogonal to the unit circle: Re(c·w̄) = (1 + |w|²)/2 for w ∈ {Q, R}.
  const cxv = (1 + Rr * Rr) / (2 * Rr);
  const cyv = ((1 + Q.re * Q.re + Q.im * Q.im) / 2 - cxv * Q.re) / Q.im;
  const center = cx(cxv, cyv);
  const radius = Math.sqrt(cxv * cxv + cyv * cyv - 1);
  const n1 = cx(-Math.sin(A), Math.cos(A));
  const mirrors: [Isometry, Isometry, Isometry] = [
    REFLECT_REAL,
    reflectLine(A),
    reflectCircle(center, radius),
  ];
  const kq = Math.tanh(lengthPQ);
  return {
    p,
    q,
    r,
    P: cx(0, 0),
    Q,
    R,
    n1,
    center,
    radius,
    lengthPR,
    lengthPQ,
    lengthQR,
    area: Math.PI * (1 - 1 / p - 1 / q - 1 / r),
    mirrors,
    kleinQ: fromPolar(kq, A),
    kleinR: Math.tanh(lengthPR),
  };
}

/** Signed violation of each mirror for point (x, y): positive means "outside F across it". */
export function mirrorViolation(
  tg: TriangleGroup,
  x: number,
  y: number,
  mirror: 0 | 1 | 2,
): number {
  if (mirror === 0) return -y;
  if (mirror === 1) return x * tg.n1.re + y * tg.n1.im;
  const dx = x - tg.center.re;
  const dy = y - tg.center.im;
  return tg.radius * tg.radius - (dx * dx + dy * dy);
}

export function insideTriangle(tg: TriangleGroup, z: Complex, eps = 1e-12): boolean {
  return (
    mirrorViolation(tg, z.re, z.im, 0) <= eps &&
    mirrorViolation(tg, z.re, z.im, 1) <= eps &&
    mirrorViolation(tg, z.re, z.im, 2) <= eps
  );
}

/** Inside the domain of the rotation group: the triangle together with its mirror image in m0. */
export function insideRotationDomain(tg: TriangleGroup, z: Complex, eps = 1e-12): boolean {
  return insideTriangle(tg, { re: z.re, im: Math.abs(z.im) }, eps);
}

export interface FoldResult {
  /** The folded point: in F (reflection) or in W = F ∪ m0(F) (rotation). */
  z: Complex;
  /** Number of reflections used, mod 2, before the rotation-mode correction. */
  parity: 0 | 1;
  /** Mirror indices in the order they were applied (only when `recordWord`). */
  word: number[];
  /** How many reflections in m2 were needed (0 ⇔ the point lies in the central tile). */
  m2Count: number;
  iterations: number;
  converged: boolean;
}

export interface FoldOptions {
  rotation?: boolean;
  maxIterations?: number;
  recordWord?: boolean;
}

/**
 * The fold: reflect the point in any side of the fundamental triangle it lies beyond, until it
 * lies inside. Each such reflection strictly decreases the hyperbolic distance to any interior
 * point of the triangle, and orbits are discrete, so the loop terminates. With `rotation`, an
 * odd number of reflections is corrected by one more reflection in m0, so the composite map is
 * a rotation and the point lands in W = F ∪ m0(F).
 *
 * This is the same loop the fragment shader runs for every pixel.
 */
export function fold(tg: TriangleGroup, z: Complex, opts: FoldOptions = {}): FoldResult {
  const maxIterations = opts.maxIterations ?? 10000;
  let x = z.re;
  let y = z.im;
  const nx = tg.n1.re;
  const ny = tg.n1.im;
  const cxv = tg.center.re;
  const cyv = tg.center.im;
  const r2 = tg.radius * tg.radius;
  const word: number[] = [];
  let count = 0;
  let m2Count = 0;
  let converged = false;
  let it = 0;
  for (; it < maxIterations; it++) {
    if (y < 0) {
      y = -y;
      if (opts.recordWord) word.push(0);
    } else {
      const d = x * nx + y * ny;
      if (d > 0) {
        x -= 2 * d * nx;
        y -= 2 * d * ny;
        if (opts.recordWord) word.push(1);
      } else {
        const dx = x - cxv;
        const dy = y - cyv;
        const q = dx * dx + dy * dy;
        if (q < r2) {
          const k = r2 / q;
          x = cxv + dx * k;
          y = cyv + dy * k;
          m2Count++;
          if (opts.recordWord) word.push(2);
        } else {
          converged = true;
          break;
        }
      }
    }
    count++;
  }
  const parity = (count & 1) as 0 | 1;
  if (opts.rotation && parity === 1) {
    y = -y;
    if (opts.recordWord) word.push(0);
  }
  return { z: { re: x, im: y }, parity, word, m2Count, iterations: it, converged };
}

/** The isometry g = m_{w_n} ∘ … ∘ m_{w_1} for a word of mirror indices (applied left to right). */
export function wordToIsometry(tg: TriangleGroup, word: readonly number[]): Isometry {
  let g = IDENTITY;
  for (const i of word) g = compose(tg.mirrors[i], g);
  return g;
}

/**
 * Folds z and returns the group element g with g(z) = folded point, as an isometry.
 * In rotation mode g is orientation preserving.
 */
export function foldToIsometry(
  tg: TriangleGroup,
  z: Complex,
  rotation: boolean,
): { g: Isometry; folded: Complex; parity: 0 | 1; m2Count: number } {
  const res = fold(tg, z, { rotation, recordWord: true });
  return {
    g: wordToIsometry(tg, res.word),
    folded: res.z,
    parity: res.parity,
    m2Count: res.m2Count,
  };
}

/** Inradius and circumradius of the regular p-gon tile of {p, q} (r = 2), Coxeter's formulas. */
export function regularTileRadii(p: number, q: number) {
  return {
    circumradius: Math.acosh(1 / (Math.tan(Math.PI / p) * Math.tan(Math.PI / q))),
    inradius: Math.acosh(Math.cos(Math.PI / q) / Math.sin(Math.PI / p)),
    halfEdge: Math.acosh(Math.cos(Math.PI / p) / Math.sin(Math.PI / q)),
  };
}

/** Distance (signed, hyperbolic) from a point to the geodesic carried by m2; > 0 on F's side. */
export function signedDistanceToM2(tg: TriangleGroup, z: Complex): number {
  const dx = z.re - tg.center.re;
  const dy = z.im - tg.center.im;
  const s =
    (dx * dx + dy * dy - tg.radius * tg.radius) / (tg.radius * (1 - (z.re * z.re + z.im * z.im)));
  return Math.asinh(s);
}

/** Presets: regular tilings {p, q} are the (p, q, 2) triangle groups. */
export const PRESETS: readonly { p: number; q: number; label: string }[] = [
  { p: 7, q: 3, label: '{7,3}' },
  { p: 5, q: 4, label: '{5,4}' },
  { p: 4, q: 5, label: '{4,5}' },
  { p: 6, q: 4, label: '{6,4}' },
  { p: 8, q: 3, label: '{8,3}' },
  { p: 3, q: 7, label: '{3,7}' },
];
